// ""// Updated Overview.jsx with skeleton loaders for datasets and experiments

import React, { useEffect, useState, useContext } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { Link } from "react-router-dom";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Button,
  Grid,
  Typography,
  IconButton,
  Alert,
  AlertTitle,
  Tooltip,
  DialogContent,
  DialogActions,
  Dialog,
  Paper,
  Box,
  ListItem,
  List,
  ListItemButton,
  CircularProgress,
  Divider,
  Skeleton,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { useTranslation } from "react-i18next";
import { UserContext } from "../App";
import { deleteData, fetchData } from "../utils/api";
import { makeStringReadable } from "../utils/table-utils";
import { reactMUILocale } from "../i18n";

export default function Overview() {
  const theme = useTheme();
  const { t } = useTranslation();

  const {
    datasets,
    fetchDatasets,
    setCurrentDatasetIdx,
    setCurrentExperimentIdx,
  } = useContext(UserContext);

  const [experimentData, setExperimentData] = useState([]);
  const [columnInfo, setColumnInfo] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedExperiments, setSelectedExperiments] = useState([]);
  const [deleteType, setDeleteType] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  const [deleteClicked, setDeleteClicked] = useState(false);
  const [loadingDatasets, setLoadingDatasets] = useState(true);
  const [loadingExperiments, setLoadingExperiments] = useState(true);
  const [datasetToDeleteIdx, setDatasetToDeleteIdx] = useState(null);

  const fetchExperiments = async (datasetName) => {
    setLoadingExperiments(true);
    const dataset = await fetchData(
      `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/experiments`
    );
    const experimentsList = [];

    if (dataset.experiments.length !== 0) {
      dataset.experiments.forEach((experiment) => {
        const updatedExperiment = {
          ...experiment,
          is_created: experiment.experimentProgress.created,
          is_processed: experiment.experimentProgress.processed,
          num_trained_models: experiment.experimentProgress.trained,
        };
        delete updatedExperiment.experimentProgress;
        experimentsList.push(updatedExperiment);
      });

      setExperimentData(experimentsList);
      const columnNames = Object.keys(experimentsList.at(0));
      const columnInfoList = columnNames.map((name) => ({
        headerName: makeStringReadable(name),
        field: name,
        width: 150,
      }));
      setColumnInfo(columnInfoList);
    } else {
      setExperimentData([]);
    }
    setLoadingExperiments(false);
  };

  const handleListItemClick = (event, index) => {
    setSelectedIndex(index);
    fetchExperiments(datasets[index].datasetName);
  };

  const handleDialogDisplay = () => setOpen(!open);

  const handleDeleteType = (type, index = null) => {
    if (type === "Dataset") setDatasetToDeleteIdx(index);
    handleDialogDisplay();
    setDeleteType(type);
  };

  const handleDatasetDelete = async (datasetName) => {
    try {
      const data = await deleteData(
        `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}`
      );
      if (data.status === 200) {
        await fetchDatasets({});
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        setDeleteClicked(false);
      } else {
        setError(true);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleExperimentsDelete = async (datasetName, experimentNames) => {
    try {
      const deletePromises = experimentNames.map((experimentName) =>
        deleteData(
          `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/experiment/${experimentName}`
        )
      );
      const responses = await Promise.all(deletePromises);
      const allSuccessful = responses.every((res) => res.status === 200);
      if (allSuccessful) {
        fetchExperiments(datasetName);
      } else {
        setError(true);
      }
    } catch (e) {
      console.log("error", e);
    }
  };

  const handleDelete = () => {
    handleDialogDisplay();
    if (deleteType === "Experiment") {
      const experimentNames = selectedExperiments.map(
        (idx) => experimentData[idx].experimentName
      );
      handleExperimentsDelete(
        datasets[selectedIndex].datasetName,
        experimentNames
      );
    } else if (deleteType === "Dataset") {
      handleDatasetDelete(datasets[datasetToDeleteIdx].datasetName);
    }
  };

  const handleButtonClick = () => {
    setDeleteClicked(true);
    handleDelete();
  };

  useEffect(() => {
    const loadData = async () => {
      setLoadingDatasets(true);
      await fetchDatasets({});
      setLoadingDatasets(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    if (datasets.length >= 1) {
      fetchExperiments(datasets[selectedIndex].datasetName);
    } else {
      setExperimentData([]);
    }
  }, [datasets]);

  useEffect(() => {
    setCurrentDatasetIdx(selectedIndex);
    setCurrentExperimentIdx(0);
  }, [selectedIndex]);

  return (
    <Grid
      id="overview-container"
      container
      columnSpacing={4}
      rowSpacing={2}
      sx={{ padding: theme.spacing(4, 2) }}
    >
      <Grid item xs={12} md={4}>
        <Paper elevation={3} sx={{ p: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6">
              {t("overview.MyDatasetsTitle")}
            </Typography>
            <Link to="/new-dataset" id="add-dataset-topbar-link">
              <Tooltip title={t("overview.DataButton")}>
                <IconButton aria-label={t("overview.DataButton")}>
                  <PostAddIcon />
                </IconButton>
              </Tooltip>
            </Link>
          </Box>
          <List sx={{ maxHeight: 600, overflow: "auto" }}>
            {loadingDatasets
              ? Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton
                    variant="rectangular"
                    height={50}
                    key={`skeleton-ds-${i}`}
                    sx={{ mb: 1, borderRadius: 1 }}
                  />
                ))
              : datasets.map((ds, index) => (
                  <React.Fragment key={`ds-${ds.datasetName}`}>
                    <ListItem selected={selectedIndex === index} disablePadding>
                      <ListItemButton
                        onClick={(e) => handleListItemClick(e, index)}
                      >
                        <Grid
                          container
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Grid item sx={{ maxWidth: "80%" }}>
                            <Typography noWrap>
                              {index + 1}. {ds.datasetName}
                            </Typography>
                          </Grid>
                          <Grid item>
                            {!deleteClicked ? (
                              <Tooltip
                                title={t("overview.DeleteDatasetButton")}
                              >
                                <IconButton
                                  sx={{ color: "inherit" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteType("Dataset", index);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <CircularProgress size={20} />
                            )}
                          </Grid>
                        </Grid>
                      </ListItemButton>
                    </ListItem>
                    <Divider variant="inset" component="li" />
                  </React.Fragment>
                ))}
          </List>
        </Paper>
      </Grid>

      <Grid item xs={12} md={8}>
        <Paper elevation={3} sx={{ p: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
            flexWrap="wrap"
          >
            <Typography variant="h6">
              {t("overview.MyExperimentsTitle")}
            </Typography>
            <Box display="flex" alignItems="center">
              <Link to="/new-experiment" id="add-experiment-topbar-link">
                <Tooltip title={t("overview.ExperimentButton")}>
                  <IconButton aria-label={t("overview.ExperimentButton")}>
                    <AddCircleIcon />
                  </IconButton>
                </Tooltip>
              </Link>
              {selectedExperiments.length > 0 && (
                <Tooltip title={t("overview.DeleteExperimentButton")}>
                  <IconButton onClick={() => handleDeleteType("Experiment")}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
          {loadingExperiments ? (
            <Box>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                {["Name", "Created", "Processed", "Models"].map(
                  (header, idx) => (
                    <Grid item xs={3} key={idx}>
                      <Skeleton variant="text" height={40} width="80%" />
                    </Grid>
                  )
                )}
              </Grid>
              {/* Rows */}
              {Array.from({ length: 4 }).map((_, rowIndex) => (
                <Grid
                  container
                  spacing={1}
                  key={rowIndex}
                  sx={{ mb: 1 }}
                  alignItems="center"
                >
                  <Grid item xs={3}>
                    <Skeleton variant="text" height={30} />
                  </Grid>
                  <Grid item xs={3}>
                    <Skeleton variant="text" height={30} />
                  </Grid>
                  <Grid item xs={3}>
                    <Skeleton variant="text" height={30} />
                  </Grid>
                  <Grid item xs={3}>
                    <Skeleton variant="text" height={30} />
                  </Grid>
                </Grid>
              ))}
            </Box>
          ) : (
            <DataGrid
              rows={experimentData}
              columns={columnInfo}
              pageSize={9}
              rowsPerPageOptions={[5]}
              localeText={reactMUILocale}
              getRowId={(row) => experimentData.indexOf(row)}
              components={{ Toolbar: GridToolbar }}
              onSelectionModelChange={(selected) =>
                setSelectedExperiments(selected)
              }
              autoHeight
              checkboxSelection
              disableRowSelectionOnClick
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: theme.palette.grey[100],
                },
                borderRadius: 1,
              }}
            />
          )}
        </Paper>
      </Grid>

      {error && (
        <Grid item xs={12} md={4}>
          <Alert severity="error" sx={{ mt: 2 }} variant="filled">
            {t("experimentErrors.FailedDeletion")}
          </Alert>
        </Grid>
      )}

      <Dialog
        open={open}
        onClose={handleDialogDisplay}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <Alert
            severity="warning"
            className="abortUploadWarning"
            icon={
              <WarningAmberIcon
                sx={{ width: 48, height: 48, color: theme.palette.error.main }}
              />
            }
          >
            <AlertTitle>
              <Typography variant="h5" color="text.secondary">
                {t("common.Warning")}
              </Typography>
            </AlertTitle>
            <Typography variant="body1" color="text.tertiary">
              {deleteType === "Dataset"
                ? t("overview.DeleteDatasetWarning")
                : t("overview.DeleteExperimentWarning")}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleButtonClick} autoFocus>
            {t("common.Agree")}
          </Button>
          <Button variant="outlined" onClick={handleDialogDisplay}>
            {t("common.Disagree")}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
